/**
 * Quick knife (08 §3): one tap draws the knife, stabs once and puts back what he was holding.
 * The input layer can only speak in `Command`s, so the tap becomes a short script over
 * successive ticks. Pure.
 *
 * What the sim needs (src/sim/ranger/weapons.ts): `select` is applied before anything else in a
 * tick; a stab wants a fire PRESS (`fire` true after a tick of false); and nothing fires while
 * `fireCooldown` runs - six ticks after a carbine round, fifteen after a pistol round. So: a tick
 * with the knife selected and the trigger released, then presses on alternate ticks. The first
 * one the cooldown lets through stabs and starts the knife's own 18-tick cooldown, which swallows
 * the rest - the presses stop before it can run out - so there is exactly one stab. A little
 * later the old weapon is selected again. After a throw or a LAW his hands are busy for longer
 * than the script lasts, and the tap does nothing.
 */

import type { WeaponId } from '../sim/types';

/** Ticks of alternating press / release after the draw: eight press edges, the last on tick 15 (< KNIFE.cooldown). */
export const STAB_TICKS = 16;
/** Tick of the script on which the old weapon comes back: the knife has been seen in his hand by then. */
export const RETURN_TICK = 24;
/** The draw was refused (a downed ranger keeps his carbine): give up after this many ticks. */
const REFUSED_TICKS = 3;

/** What the script wants this tick in place of the player's own `select` and `fire`. */
export interface KnifeOverride {
  select: WeaponId | null;
  fire: boolean;
}

export class QuickKnife {
  /** Tick of the running script; -1 when idle. */
  private tick = -1;
  private previous: WeaponId = 'carbine';

  get busy(): boolean {
    return this.tick >= 0;
  }

  cancel(): void {
    this.tick = -1;
  }

  /**
   * Once per sim tick. `tapped` is this tick's press of the quick-knife key, `current` the weapon
   * in his hands before this tick's command is applied. Null = the player's own inputs stand.
   */
  update(tapped: boolean, current: WeaponId): KnifeOverride | null {
    if (this.tick < 0) {
      if (!tapped) return null;
      this.previous = current;
      this.tick = 0;
      return { select: current === 'knife' ? null : 'knife', fire: false };
    }
    const t = ++this.tick;
    if (current !== 'knife') {
      // Never pull the trigger of whatever he is still holding.
      if (t < REFUSED_TICKS) return { select: null, fire: false };
      this.tick = -1;
      return null;
    }
    if (t <= STAB_TICKS) return { select: null, fire: t % 2 === 1 };
    const done = this.previous === 'knife' ? t > STAB_TICKS : t >= RETURN_TICK;
    if (!done) return { select: null, fire: false };
    this.tick = -1;
    return { select: this.previous === 'knife' ? null : this.previous, fire: false };
  }
}
